import React, { useMemo, useState, useEffect } from 'react';
import Pager, { usePaged } from './Pager';
import StatePill from '../StatePill';
import { initialsOf } from '../../utils/locations';
import { Tile, Segmented, TILE_GRID } from './LocationsIndex';

// Employee assignments — devices signed out to a named person rather than to a
// site, per the Locations redesign.
//
// The mockup lists one row per device under each person. The stored model is
// batch-shaped instead: one assignment record holds several deviceIds and a
// returnedDeviceIds list for partial returns. So the rows here are flattened out
// of those records for display; nothing about the stored shape changes, and a
// return still goes back through returnAssignment(assignmentId, deviceIds).

const PERSON_COLS = 'minmax(0,1.6fr) 150px 120px 130px minmax(0,1fr) 36px';
const DEV_COLS = '118px 64px 130px minmax(0,1fr) auto';

// Past this, a device has been out long enough to be worth chasing.
const LONG_HELD_DAYS = 14;

const daysSince = iso => {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / (1000 * 60 * 60 * 24)));
};

function LocationsPeople({
  st, onReturn, onNewAssignment, onTransfer, onEditEmployee, isReadOnly,
}) {
  const [query, setQuery] = useState('');
  const [holding, setHolding] = useState('All');
  const [expanded, setExpanded] = useState([]);
  const [page, setPage] = useState(1);

  const devById = useMemo(() => {
    const m = {};
    (st.devices || []).forEach(d => { m[d.id] = d; });
    return m;
  }, [st.devices]);

  // Flatten assignments into one row per device still out with someone.
  const people = useMemo(() => {
    const open = (st.assignments || []).filter(a => !a.returnedAt);
    return (st.employees || []).map(emp => {
      const heldRows = [];
      open.filter(a => a.employeeId === emp.id).forEach(a => {
        const returned = a.returnedDeviceIds || [];
        (a.deviceIds || []).forEach(id => {
          if (returned.includes(id)) return;
          const d = devById[id];
          heldRows.push({
            assignmentId: a.id,
            deviceId: id,
            serial: (d && d.serial) || id,
            type: (d && d.type) || '—',
            since: a.assignedAt,
            days: daysSince(a.assignedAt),
            purpose: a.purpose || 'Not recorded',
            expectedReturn: a.expectedReturn || '',
            // A device flagged lost or in RMA while signed out is worth calling out.
            flagged: !!d && (d.availability === 'lost' || d.availability === 'rma' || d.status === 'Quarantine'),
          });
        });
      });
      const longest = heldRows.reduce((n, r) => Math.max(n, r.days), 0);
      const flags = [];
      const flaggedCount = heldRows.filter(r => r.flagged).length;
      if (flaggedCount) flags.push({ label: `${flaggedCount} flagged`, tone: 'danger' });
      if (longest > LONG_HELD_DAYS) flags.push({ label: `Out ${longest} days`, tone: 'warning' });
      return { emp, held: heldRows, longest, flags };
    });
  }, [st.assignments, st.employees, devById]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people.filter(p => {
      if (holding === 'Holding' && p.held.length === 0) return false;
      if (holding === 'Flagged' && p.flags.length === 0) return false;
      if (!q) return true;
      const hay = `${p.emp.name || ''} ${p.emp.role || ''} ${p.emp.team || ''} ${p.held.map(r => r.serial).join(' ')}`;
      return hay.toLowerCase().includes(q);
    });
  }, [people, query, holding]);

  // Search/filter changes return you to page 1 (see Pager.js).
  useEffect(() => { setPage(1); }, [query, holding]);

  const { paged, page: safePage, pageCount, from, to } = usePaged(filtered, page, setPage);

  const devicesOut = people.reduce((n, p) => n + p.held.length, 0);
  const holdingCount = people.filter(p => p.held.length).length;
  const overdue = people.reduce((n, p) => n + p.held.filter(r => r.days > LONG_HELD_DAYS).length, 0);
  const flaggedTotal = people.reduce((n, p) => n + p.held.filter(r => r.flagged).length, 0);

  const toggle = id => setExpanded(x => (x.includes(id) ? x.filter(v => v !== id) : x.concat(id)));

  return (
    <>
      <div style={TILE_GRID}>
        <Tile label="Devices with staff" value={devicesOut} sub="signed out, not on a site" />
        <Tile label="People holding devices" value={holdingCount} sub={`of ${(st.employees || []).length} on the list`} />
        <Tile label={`Out over ${LONG_HELD_DAYS} days`} value={overdue} tone={overdue ? 'warning' : undefined} sub="worth chasing back" />
        <Tile label="Flagged" value={flaggedTotal} tone={flaggedTotal ? 'warning' : undefined} sub="lost, quarantined or in RMA" />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', marginTop: 16, display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: 52, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--surface-default)', width: 280, maxWidth: '100%' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, color: 'var(--text-muted)' }}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search person, team or serial"
              aria-label="Search people"
              style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, color: 'var(--text-primary)' }} />
          </div>
          <div style={{ flex: 1 }} />
          <Segmented
            options={[['All', 'Everyone'], ['Holding', 'Holding devices'], ['Flagged', 'Flagged']]}
            value={holding}
            onChange={setHolding} />
        </div>

        <div style={{ flexShrink: 0, display: 'grid', gridTemplateColumns: PERSON_COLS, gap: '0 16px', height: 36, alignItems: 'center', padding: '0 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
          <div>Person</div><div>Team</div>
          <div style={{ textAlign: 'right' }}>Devices held</div>
          <div style={{ textAlign: 'right' }}>Longest held</div>
          <div>Flags</div><div />
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {paged.map(p => {
            const open = expanded.includes(p.emp.id);
            return (
              <div key={p.emp.id}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  onClick={() => toggle(p.emp.id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(p.emp.id); } }}
                  style={{ display: 'grid', gridTemplateColumns: PERSON_COLS, gap: '0 16px', alignItems: 'center', minHeight: 56, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', background: open ? 'var(--surface-inset)' : 'transparent' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = open ? 'var(--surface-inset)' : 'transparent'; }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <div aria-hidden="true" style={{ flexShrink: 0, width: 26, height: 26, borderRadius: 6, background: p.held.length ? 'var(--brand-default)' : 'var(--text-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600, color: '#fff' }}>
                      {initialsOf(p.emp.name)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.emp.name || 'Unnamed'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{p.emp.role || 'No role'}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.emp.team || '—'}</div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: p.held.length ? 'var(--text-primary)' : 'var(--text-muted)' }}>{p.held.length}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{p.held.length ? 'signed out' : 'none held'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: p.longest > LONG_HELD_DAYS ? 'var(--warning-text)' : 'var(--text-primary)' }}>{p.longest || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{p.longest ? 'days' : 'nothing out'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexWrap: 'wrap' }}>
                    {p.flags.length
                      ? p.flags.map(fl => <StatePill key={fl.label} state={fl.label} tone={fl.tone} />)
                      : <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>None</span>}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--text-subtle)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}><polyline points="9 18 15 12 9 6" /></svg>
                  </div>
                </div>

                {open && (
                  <div style={{ background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
                    {p.held.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: DEV_COLS, gap: '0 14px', height: 32, alignItems: 'center', padding: '0 16px 0 54px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                        <div>Serial</div><div>Type</div><div>Signed out</div><div>What for</div><div />
                      </div>
                    )}
                    {p.held.map(r => (
                      <div key={r.deviceId} style={{ display: 'grid', gridTemplateColumns: DEV_COLS, gap: '0 14px', alignItems: 'center', minHeight: 46, padding: '7px 16px 7px 54px', borderTop: '1px solid var(--border-subtle)' }}>
                        <div className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{r.serial}</div>
                        <div><span className="pill pill-neutral" style={{ fontSize: 11.5 }}>{r.type}</span></div>
                        <div>
                          <div className="tnum" style={{ fontSize: 14, color: 'var(--text-primary)' }}>
                            {r.since ? new Date(r.since).toLocaleDateString('en-GB') : '—'}
                          </div>
                          <div className="tnum" style={{ fontSize: 12, marginTop: 2, color: r.days > LONG_HELD_DAYS ? 'var(--warning-text)' : 'var(--text-muted)' }}>
                            {r.days} day{r.days === 1 ? '' : 's'}
                          </div>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.purpose}</div>
                          <div style={{ fontSize: 12, marginTop: 2, color: r.flagged ? 'var(--danger-text)' : 'var(--text-muted)' }}>
                            {r.flagged ? 'Flagged — check the device record' : 'Signed out'}
                          </div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, flexWrap: 'wrap' }}>
                          {!isReadOnly && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onReturn(r.assignmentId, [r.deviceId])}>
                              Return
                            </button>
                          )}
                          {!isReadOnly && onTransfer && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onTransfer(r.serial, p.emp.id)}>
                              Transfer
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                    {p.held.length === 0 && (
                      <div style={{ padding: '16px 16px 16px 54px', borderTop: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>Holding nothing at the moment.</span>
                        {!isReadOnly && onNewAssignment && (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onNewAssignment(p.emp.id)}>
                            Sign a device out to {(p.emp.name || '').split(' ')[0] || 'them'}
                          </button>
                        )}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px 10px 54px', borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
                      <span style={{ flex: 1, fontSize: 12, color: 'var(--text-muted)' }}>
                        {[p.emp.role, p.emp.base, p.emp.phone].filter(Boolean).join(' · ') || 'No contact details recorded'}
                      </span>
                      {!isReadOnly && onEditEmployee && (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => onEditEmployee(p.emp)}>Edit employee</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div style={{ padding: '40px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>No one matches</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
                {(st.employees || []).length === 0
                  ? 'No employees yet — add one to sign devices out to a person.'
                  : 'Try a different name, team or serial.'}
              </div>
            </div>
          )}
        </div>

        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, minHeight: 36, padding: '4px 16px', background: 'var(--surface-inset)', borderTop: '1px solid var(--border-default)' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {filtered.length === 0
              ? 'No people'
              : `${from}–${to} of ${filtered.length}${filtered.length === people.length ? '' : ` (filtered from ${people.length})`} ${filtered.length === 1 ? 'person' : 'people'}${filtered.length === people.length ? ` · ${devicesOut} device${devicesOut === 1 ? '' : 's'} signed out` : ''}`}
          </span>
          <div style={{ marginLeft: 'auto' }}>
            <Pager page={safePage} pageCount={pageCount} setPage={setPage} label="People" />
          </div>
        </div>
      </div>
    </>
  );
}

export default LocationsPeople;
